import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, TrashSimple, PencilSimple, MapPin, WifiHigh } from "@phosphor-icons/react";
import StoreMap from "../components/StoreMap";

const empty = {
  name: "", address: "", latitude: 0, longitude: 0,
  radius_meters: 100, opening_time: "08:00", closing_time: "20:00",
  timezone: "UTC", allowed_ips: "",
};

const M_PER_MILE = 1609.344;

// A small curated list of common time zones (Intl.supportedValuesOf isn't on all browsers)
const TIMEZONES = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Mexico_City", "America/Sao_Paulo",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Rome",
  "Europe/Athens", "Europe/Moscow",
  "Africa/Lagos", "Africa/Cairo", "Africa/Johannesburg", "Africa/Nairobi",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Bangkok",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
];

export default function Stores() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [unit, setUnit] = useState("m"); // 'm' | 'mi'

  const load = async () => {
    try {
      const { data } = await api.get("/stores");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const radiusDisplay = unit === "mi"
    ? (Number(form.radius_meters) / M_PER_MILE).toFixed(2)
    : Math.round(Number(form.radius_meters));

  const setRadiusDisplay = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return;
    setForm((f) => ({ ...f, radius_meters: unit === "mi" ? Math.round(n * M_PER_MILE) : Math.round(n) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        radius_meters: parseInt(form.radius_meters, 10),
        allowed_ips: (form.allowed_ips || "")
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editingId) {
        await api.patch(`/stores/${editingId}`, payload);
        toast.success("Store updated");
      } else {
        await api.post("/stores", payload);
        toast.success("Store created");
      }
      setForm(empty);
      setEditingId(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setForm((f) => ({ ...f, latitude: p.coords.latitude, longitude: p.coords.longitude })),
      (err) => toast.error(err.message)
    );
  };

  const useMyIp = async () => {
    try {
      const r = await fetch("https://api.ipify.org?format=json");
      const j = await r.json();
      setForm((f) => ({
        ...f,
        allowed_ips: (f.allowed_ips ? f.allowed_ips + "\n" : "") + j.ip,
      }));
      toast.success(`Added your IP: ${j.ip}`);
    } catch {
      toast.error("Could not fetch your IP");
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name, address: s.address,
      latitude: s.latitude, longitude: s.longitude,
      radius_meters: s.radius_meters,
      opening_time: s.opening_time, closing_time: s.closing_time,
      timezone: s.timezone || "UTC",
      allowed_ips: (s.allowed_ips || []).join("\n"),
    });
    setShowForm(true);
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/stores/${s.id}`); toast.success("Store deleted"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="stores-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Locations</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Stores</h1>
          <p className="text-zinc-600 mt-1 text-sm">{items.length} stores configured.</p>
        </div>
        <button
          data-testid="new-store-btn"
          onClick={() => { setEditingId(null); setForm(empty); setShowForm((v) => !v); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm"
        >
          <Plus size={14} weight="bold" />
          New Store
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid lg:grid-cols-2 gap-6" data-testid="store-form">
          {/* Left column: fields */}
          <div className="space-y-4">
            <div className="label-eyebrow">{editingId ? "Edit store" : "New store"}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lom-input" data-testid="store-name-input" /></Field>
              <Field label="Address"><input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="lom-input" /></Field>
              <Field label="Latitude"><input type="number" step="any" required value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="lom-input font-mono" data-testid="store-lat-input" /></Field>
              <Field label="Longitude"><input type="number" step="any" required value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="lom-input font-mono" data-testid="store-lng-input" /></Field>
            </div>
            <button type="button" onClick={useMyLocation} className="flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-sm text-sm hover:bg-zinc-50" data-testid="use-my-location-btn">
              <MapPin size={14} weight="bold" />
              Use my current location
            </button>

            <Field label="Geofence radius">
              <div className="flex gap-2">
                <input
                  type="number"
                  step={unit === "mi" ? 0.01 : 1}
                  min={unit === "mi" ? 0.01 : 10}
                  required
                  value={radiusDisplay}
                  onChange={(e) => setRadiusDisplay(e.target.value)}
                  className="lom-input flex-1"
                  data-testid="store-radius-input"
                />
                <div className="flex border border-zinc-300 rounded-sm overflow-hidden">
                  {["m", "mi"].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setUnit(u)}
                      className={`px-3 text-xs font-bold uppercase tracking-wider ${unit === u ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
                      data-testid={`unit-${u}`}
                    >
                      {u === "m" ? "meters" : "miles"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                = {form.radius_meters} m · {(form.radius_meters / M_PER_MILE).toFixed(2)} mi
              </div>
            </Field>

            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Timezone">
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className="lom-input"
                  data-testid="store-tz-select"
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </Field>
              <Field label="Opening time"><input type="time" required value={form.opening_time} onChange={(e) => setForm({ ...form, opening_time: e.target.value })} className="lom-input" /></Field>
              <Field label="Closing time"><input type="time" required value={form.closing_time} onChange={(e) => setForm({ ...form, closing_time: e.target.value })} className="lom-input" /></Field>
            </div>

            <Field label={
              <span className="flex items-center gap-2">
                <WifiHigh size={12} weight="bold" />
                Store network IPs (one per line, optional)
              </span>
            }>
              <textarea
                value={form.allowed_ips}
                onChange={(e) => setForm({ ...form, allowed_ips: e.target.value })}
                rows={3}
                className="lom-input font-mono"
                placeholder={"e.g. 203.0.113.42\n203.0.113.0/24"}
                data-testid="store-allowed-ips"
              />
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-zinc-500">If set, clock-in must come from one of these public IPs (CIDR ok). Leave blank to disable.</span>
                <button type="button" onClick={useMyIp} className="text-xs underline text-zinc-700" data-testid="use-my-ip-btn">Add my current IP</button>
              </div>
            </Field>

            <div className="flex gap-2 justify-end pt-2 border-t border-zinc-100">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="store-submit-btn">
                {editingId ? "Save changes" : "Create store"}
              </button>
            </div>
          </div>

          {/* Right column: map */}
          <div>
            <div className="label-eyebrow mb-2">Click map to place store</div>
            <StoreMap
              latitude={form.latitude}
              longitude={form.longitude}
              radiusMeters={parseInt(form.radius_meters, 10) || 0}
              onPick={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              height={420}
            />
            <div className="text-xs text-zinc-500 mt-2 font-mono">
              {form.latitude && form.longitude
                ? `📍 ${Number(form.latitude).toFixed(5)}, ${Number(form.longitude).toFixed(5)}`
                : "No pin set — click anywhere on the map."}
            </div>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid={`store-card-${s.id}`}>
            <StoreMap
              latitude={s.latitude}
              longitude={s.longitude}
              radiusMeters={s.radius_meters}
              interactive={false}
              height={150}
              mapKey={s.id}
            />
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-heading font-bold text-lg text-zinc-950">{s.name}</div>
                  <div className="text-sm text-zinc-600">{s.address}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(s)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`edit-store-${s.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(s)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-store-${s.id}`}><TrashSimple size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><div className="label-eyebrow">Radius</div><div className="font-mono mt-0.5">{s.radius_meters}m · {(s.radius_meters / M_PER_MILE).toFixed(2)}mi</div></div>
                <div><div className="label-eyebrow">Timezone</div><div className="font-mono mt-0.5 truncate">{s.timezone || "UTC"}</div></div>
                <div><div className="label-eyebrow">Hours</div><div className="font-mono mt-0.5">{s.opening_time}–{s.closing_time}</div></div>
                <div>
                  <div className="label-eyebrow flex items-center gap-1"><WifiHigh size={10} weight="bold" /> Network</div>
                  <div className="font-mono mt-0.5">{s.allowed_ips?.length ? `${s.allowed_ips.length} IP${s.allowed_ips.length > 1 ? "s" : ""}` : "Any"}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><label className="label-eyebrow block mb-1.5">{label}</label>{children}</div>
);
